import currencyData from '../../data/currencies.json';

// Universal Currency Formatter with Locale Support Patch
function formatCurrency(amount: number, locale: string, currency: string): string {
  const currencySymbols: Record<string, string> = {
    AUD: 'A$',
    CAD: 'CA$',
    USD: '$',
  };

  try {
    const formatter = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
    });

    let formatted = formatter.format(amount);

    // Force the correct currency symbol based on currency code
    if (currency in currencySymbols) {
      // Get the correct currency symbol
      const correctSymbol = currencySymbols[currency];

      // Replace the currency symbol part while preserving the locale-specific number formatting
      // This regular expression matches any non-digit, non-comma, non-dot, non-space at the beginning
      formatted = formatted.replace(/^[^\d\s.,]+/, correctSymbol);
    }

    return formatted;
  } catch (error) {
    console.error('Intl.NumberFormat Error:', error);
    // Fallback if Intl.NumberFormat completely fails
    return `${currency in currencySymbols ? currencySymbols[currency] : ''}${amount.toFixed(2)}`;
  }
}

/**
 * Formats the amount in minor units (cents, pence, etc.) to a string based on the provided currency code and locale.
 * @param {number} amountMinorUnits - The amount in minor units (e.g., 12345 for $123.45).
 * @param {string} currencyCode - The ISO 4217 currency code (e.g., "USD", "JPY").
 * @param {string} locale - The locale to format the output (default is "en-US").
 * @returns {string} - The formatted money string (e.g., "$123.45").
 */
export function formatMoney(amountMinorUnits: number, currencyCode = 'USD', locale?: string): string {
  const safeLocale = locale || 'en-US';
  const currencyInfo = currencyData[currencyCode as keyof typeof currencyData];

  if (!currencyInfo) {
    throw new Error(`Currency ${currencyCode} not found in currency data.`);
  }

  // Convert minor units to major units
  // Note: The backend stores all amounts with 2 decimal places (multiplied by 100),
  // regardless of the currency's ISOdigits. This means:
  // - For ISOdigits: 0 (JPY, KRW, etc.) → divide by 100 (not 10^0 = 1)
  // - For ISOdigits: 2 (USD, EUR, etc.) → divide by 100 (matches 10^2)
  // - For ISOdigits: 3 (KWD, BHD, etc.) → divide by 100 (not 10^3 = 1000)
  //   Note: This means currencies with 3 decimal places lose precision (3rd decimal is lost)
  let value: number;
  if (currencyInfo.ISOdigits === 0 || currencyInfo.ISOdigits === 3) {
    // Backend stores amounts with 2 decimal places for all currencies
    // For currencies with 0 or 3 decimal places, divide by 100 to convert from backend format
    value = amountMinorUnits / 100;
  } else {
    // For currencies with 2 decimal places (most common), use the standard conversion
    value = amountMinorUnits / Math.pow(10, currencyInfo.ISOdigits);
  }

  return formatCurrency(value, safeLocale, currencyCode);
}

/**
 * Utility function to get currency information based on the ISO 4217 currency code.
 * Uses the preloaded currency data from the JSON file.
 */
export function getCurrencyInfo(currencyCode: string) {
  return currencyData[currencyCode as keyof typeof currencyData] || null;
}

/**
 * Convert a money string or number to minor units
 */
export function moneyStringOrNumberToMinorUnits(
  moneyStringOrNumber: string | number,
  currencyCode: string,
): number {
  const currencyInfo = currencyData[currencyCode as keyof typeof currencyData];

  if (!currencyInfo) {
    throw new Error(`Currency ${currencyCode} not found in currency data.`);
  }

  const value =
    typeof moneyStringOrNumber === 'string' ? parseFloat(moneyStringOrNumber) : moneyStringOrNumber;

  return Math.round(value * Math.pow(10, currencyInfo.ISOdigits));
}


export function minorUnitsToMajorUnits(amountMinorUnits: number, currencyCode: string): number {
  const currencyInfo = currencyData[currencyCode as keyof typeof currencyData];

  if (!currencyInfo) {
    throw new Error(`Currency ${currencyCode} not found in currency data.`);
  }

  // Backend stores amounts with 2 decimal places for all currencies
  // For currencies with 0 or 3 decimal places, divide by 100 to convert from backend format
  if (currencyInfo.ISOdigits === 0 || currencyInfo.ISOdigits === 3) {
    return amountMinorUnits / 100;
  }

  return amountMinorUnits / Math.pow(10, currencyInfo.ISOdigits);
}

/**
 * Format money without currency symbol, just the numeric value
 * Note: The backend stores all amounts with 2 decimal places (multiplied by 100),
 * even for currencies with 0 decimal places like JPY.
 */
export const formatMoneyWithoutSymbol = ({
  amount,
  currencyCode,
  fromCents = true,
}: {
  amount: number;
  currencyCode: string;
  fromCents?: boolean;
}): string => {
  const currencyInfo = currencyData[currencyCode as keyof typeof currencyData];
  const digits = currencyInfo?.ISOdigits || 2;

  let value: number;
  if (fromCents) {
    // Backend stores amounts with 2 decimal places for all currencies
    // For currencies with 0 or 3 decimal places, divide by 100 to convert from backend format
    const divisor = digits === 0 || digits === 3 ? 100 : Math.pow(10, digits);
    value = amount / divisor;
  } else {
    value = amount;
  }
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(value);
};

/**
 * Convert currency amounts with exchange rates
 */
export const convertCurrency = (
  amount: number,
  fromCurrency: string,
  toCurrency: string,
  rate: number,
): number => {
  if (fromCurrency === toCurrency) return amount;
  return Math.round(amount * rate);
};

/**
 * Simple money formatter for quick use (similar to the existing useCurrency format function)
 * Note: The backend stores all amounts with 2 decimal places (multiplied by 100),
 * even for currencies with 0 decimal places like JPY.
 */
export function formatSimpleMoney(amount: number, currencyCode: string): string {
  const currencyInfo = currencyData[currencyCode as keyof typeof currencyData];
  const symbol = currencyInfo?.symbol || currencyCode;
  const digits = currencyInfo?.ISOdigits || 2;

  // Backend stores amounts with 2 decimal places for all currencies
  // For currencies with 0 or 3 decimal places, divide by 100 to convert from backend format
  const divisor = digits === 0 || digits === 3 ? 100 : Math.pow(10, digits);
  return `${symbol}${(amount / divisor).toFixed(digits)}`;
}
